import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { axiosInstance } from "../../utils/axiosInstance";

type Props = {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
};

export default function HelpdeskCallLogTicketDialog({ open, onClose, onCreated }: Props) {
  const [title, setTitle] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerDisplayName, setCustomerDisplayName] = useState("");
  const [direction, setDirection] = useState<"inbound" | "outbound">("inbound");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [durationSeconds, setDurationSeconds] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setTitle("");
    setCustomerEmail("");
    setCustomerDisplayName("");
    setDirection("inbound");
    setPhoneNumber("");
    setDurationSeconds("");
    setNotes("");
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const submit = async () => {
    setError(null);
    setLoading(true);
    try {
      const durRaw = durationSeconds.trim();
      const durationParsed =
        durRaw === "" ? null : Number.parseInt(durRaw, 10);
      await axiosInstance.post("/customer-service/helpdesk/tickets/zammad/call-log", {
        title: title.trim(),
        customerEmail: customerEmail.trim(),
        customerDisplayName: customerDisplayName.trim() || null,
        direction,
        phoneNumber: phoneNumber.trim(),
        durationSeconds:
          durationParsed != null && Number.isFinite(durationParsed) ? durationParsed : null,
        notes: notes.trim() || null,
      });
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        "Failed to create call log ticket.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Log call (Zammad)
        <IconButton aria-label="close" onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box display="flex" flexDirection="column" gap={2} pt={0.5}>
          <TextField
            label="Ticket title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Customer email"
            type="email"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Customer name (optional)"
            value={customerDisplayName}
            onChange={(e) => setCustomerDisplayName(e.target.value)}
            fullWidth
            size="small"
          />
          <FormControl fullWidth size="small">
            <InputLabel id="call-dir-label">Direction</InputLabel>
            <Select
              labelId="call-dir-label"
              label="Direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value as "inbound" | "outbound")}
            >
              <MenuItem value="inbound">Inbound</MenuItem>
              <MenuItem value="outbound">Outbound</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Phone number"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            required
            fullWidth
            size="small"
          />
          <TextField
            label="Duration (seconds, optional)"
            value={durationSeconds}
            onChange={(e) => setDurationSeconds(e.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void submit()} disabled={loading}>
          Create ticket
        </Button>
      </DialogActions>
    </Dialog>
  );
}
